import React, { ReactEventHandler, ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { Collapse, Space, Row, Col, Button } from 'antd-v4'
import { ILocationZoneContract } from '@getgreenline/blaze-ecomm'
import { DownOutlined, UpOutlined } from '@ant-design/icons'
import { ButtonProps } from 'antd-v4/lib/button'
import { GrPrice, rgbaToHex, swapArrayElements } from '../../../../utilities/helpers'
import { ConfigureEcomDelivery } from './ConfigureEcomDelivery'
import { DEFAULT_FILL_COLOR, DEFAULT_LINE_COLOR } from './EcommerceSettingModels'
const { Panel } = Collapse

interface IProps {
  children?: React.ReactNode
  index: number
  locationZone: ILocationZoneContract
  configureEcomDelivery: ConfigureEcomDelivery
  disabled?: boolean
}
interface ReOrderButtonProps extends ButtonProps {
  children: ReactNode
  onClick?: ReactEventHandler<HTMLElement>
}
const _DeliveryZoneCollapse: React.FC<IProps> = ({
  index,
  locationZone,
  configureEcomDelivery,
  children,
  disabled,
}: IProps) => {
  const {
    activeKeys,
    setActiveKeys,
    allLocationZones,
    setAllLocationZones,
    locationZonesToShow,
    isPostalCodeOptionEnabled,
    isKMLOptionEnabled,
  } = configureEcomDelivery

  const reorderUp = (index: number) => {
    if (index > 0) {
      const updatedLocationZones = swapArrayElements(allLocationZones, index, index - 1)
      setAllLocationZones(updatedLocationZones, true)
    }
    setActiveKeys([])
  }

  const reorderDown = (index: number) => {
    if (index < locationZonesToShow.length - 1) {
      const updatedLocationZones = swapArrayElements(allLocationZones, index, index + 1)
      setAllLocationZones(updatedLocationZones, true)
    }
    setActiveKeys([])
  }

  const toggleCollapse = (key: string) => {
    const updatedKeys = activeKeys.includes(key)
      ? activeKeys.filter((activeKey) => activeKey !== key)
      : [...activeKeys, key]
    setActiveKeys(updatedKeys)
  }

  const ReorderButton: React.FC<ReOrderButtonProps> = ({
    children,
    onClick,
    ...rest
  }: ReOrderButtonProps) => (
    <Button
      size='small'
      shape='round'
      type='link'
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation()
        onClick && onClick(event)
      }}
      {...rest}
    >
      {children}
    </Button>
  )

  const headerPanelCollapse = (
    <>
      <Space className='header-panel-collapse'>
        <Col>
          <Row>
            <strong>Zone</strong>
          </Row>
          <Row>{locationZone.name}</Row>
        </Col>

        {isPostalCodeOptionEnabled && (
          <Col>
            <Row>
              <strong>Postal Codes</strong>
            </Row>
            <Row>{locationZone.postalCodes?.join(', ')}</Row>
          </Col>
        )}

        <Col>
          <Row>
            <strong>Fee</strong>
          </Row>
          <Row>{GrPrice.renderDollarString(locationZone.fee)}</Row>
        </Col>

        {isKMLOptionEnabled && (
          <Col style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 4,
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: locationZone.lineColor
                  ? rgbaToHex(locationZone.lineColor)
                  : DEFAULT_LINE_COLOR,
                backgroundColor: locationZone.fillColor
                  ? rgbaToHex(locationZone.fillColor)
                  : DEFAULT_FILL_COLOR,
              }}
            />
          </Col>
        )}
      </Space>
    </>
  )

  const reorderLocationZoneOptions = (
    <Space className='reorder-location-zone-options' direction='vertical'>
      <ReorderButton
        onClick={() => {
          reorderUp(index)
        }}
      >
        <UpOutlined translate='yes' />
      </ReorderButton>
      <ReorderButton
        onClick={() => {
          reorderDown(index)
        }}
      >
        <DownOutlined translate='yes' />
      </ReorderButton>
    </Space>
  )

  return (
    <Collapse activeKey={activeKeys} onChange={() => toggleCollapse(`${index}`)}>
      <Panel
        key={index}
        header={headerPanelCollapse}
        extra={reorderLocationZoneOptions}
        disabled={disabled}
      >
        {children}
      </Panel>
    </Collapse>
  )
}

export const DeliveryZoneCollapse = observer(_DeliveryZoneCollapse)
